import React, { useState } from 'react';
import { AlertTriangle, XCircle, CheckCircle2, Zap, TrendingUp, FileText, ShieldCheck, ArrowRight, ChevronDown, ChevronRight, Info } from 'lucide-react';
import { StatCard } from '../../../../shared/StatCard';
import { Badge } from '../../../../../components/ui/Badge';
import { Card } from '../../../../../components/ui/Card';
import { MODULE_CONFIG, PIPELINE_STAGES, SEVERITY_CONFIG } from '../shared';
import type { GovernanceStats, ModuleHealth, FamilyHealth, UnifiedQuestionRow } from '../hooks/useGovernanceData';
import type { IssueDomain } from '../../../../../governance';
interface OverviewDashboardProps {
  stats: GovernanceStats;
  moduleHealth: ModuleHealth[];
  familyHealthByModule: Record<string, FamilyHealth[]>;
  topBlockers: UnifiedQuestionRow[];
  pipelineIssueCounts: Record<IssueDomain, number> | null;
  isRunning: boolean;
  onNavigateToQuestion: (filter?: string) => void;
}
export function OverviewDashboard({
  stats,
  moduleHealth,
  familyHealthByModule,
  topBlockers,
  pipelineIssueCounts,
  isRunning,
  onNavigateToQuestion
}: OverviewDashboardProps) {
  const isHealthy = stats.fail === 0 && stats.review === 0;
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const toggleModule = (code: string) => {
    setExpandedModules(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);else next.add(code);
      return next;
    });
  };
  // Total findings across all pipeline stages
  const totalFindings = pipelineIssueCounts ? Object.values(pipelineIssueCounts).reduce((sum, c) => sum + c, 0) : 0;
  return <div className="space-y-6" data-id="element-3049">
      {/* Health Banner — question-level summary */}
      <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${isHealthy ? 'bg-emerald-50 border-emerald-200' : stats.fail > 0 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`} data-id="element-3050">
        {isHealthy ? <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0" data-id="element-3051" /> : stats.fail > 0 ? <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" data-id="element-3052" /> : <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" data-id="element-3053" />}
        <div className="flex-1 min-w-0" data-id="element-3054">
          <span className={`text-sm font-semibold ${isHealthy ? 'text-emerald-800' : stats.fail > 0 ? 'text-red-800' : 'text-amber-800'}`} data-id="element-3055">
            {isHealthy ? 'Alle vragen voldoen aan de kwaliteitseisen' : stats.fail > 0 ? `${stats.fail} ${stats.fail === 1 ? 'vraag' : 'vragen'} afgekeurd` : 'Aandachtspunten gevonden'}
          </span>
          {!isHealthy && <span className={`text-xs ml-2 ${stats.fail > 0 ? 'text-red-700' : 'text-amber-700'}`} data-id="element-3056">
              {[stats.review > 0 && `${stats.review} vereisen review`, totalFindings > 0 && `${totalFindings} bevindingen totaal`].filter(Boolean).join(' · ')}
            </span>}
        </div>
      </div>

      {/* Stats — question-level counts */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3057">
        <StatCard label="Totaal Vragen" value={stats.total.toLocaleString('nl-NL')} icon={<FileText className="w-5 h-5" data-id="element-3059" />} color="blue" subtitle="Unieke vragen in het systeem" data-id="element-3058" />
        <StatCard label="Geslaagd (PASS)" value={stats.pass.toLocaleString('nl-NL')} icon={<CheckCircle2 className="w-5 h-5" data-id="element-3061" />} color="emerald" subtitle={`${Math.round(stats.pass / Math.max(stats.total, 1) * 100)}% van alle vragen`} data-id="element-3060" />
        <StatCard label="Afgekeurd (FAIL)" value={stats.fail.toLocaleString('nl-NL')} icon={<XCircle className="w-5 h-5" data-id="element-3063" />} color={stats.fail === 0 ? 'emerald' : 'red'} subtitle={`${stats.blockerCount} blocker-bevindingen`} data-id="element-3062" />
        <StatCard label="Review Nodig" value={stats.review.toLocaleString('nl-NL')} icon={<AlertTriangle className="w-5 h-5" data-id="element-3065" />} color={stats.review === 0 ? 'emerald' : 'amber'} subtitle={`${stats.warningCount} verbeterpunten`} data-id="element-3064" />
      </div>

      {/* Explainer legend */}
      <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-blue-50/50 border border-blue-100" data-id="element-3066">
        <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" data-id="element-3067" />
        <div className="text-xs text-blue-800 leading-relaxed" data-id="element-3068">
          <span className="font-semibold" data-id="element-3069">Hoe lees je deze cijfers?</span>{' '}
          <span className="font-semibold text-red-700" data-id="element-3070">Afgekeurd</span> en{' '}
          <span className="font-semibold text-amber-700" data-id="element-3071">Review</span> tellen
          het aantal <em data-id="element-3072">vragen</em> met die status. Elke vraag kan meerdere{' '}
          <em data-id="element-3073">bevindingen</em> hebben (blocker = blokkerend, verbeterpunt =
          advies). De Pipeline Fasen hieronder tonen het totaal aantal
          bevindingen per controle-fase.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3074">
        {/* Left Column: Top Blockers & Pipeline Stages */}
        <div className="lg:col-span-2 space-y-6" data-id="element-3075">
          {/* Top Blockers */}
          <Card className="p-5" data-id="element-3076">
            <div className="flex items-center justify-between mb-4" data-id="element-3077">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-3078">
                <AlertTriangle className="w-4 h-4 text-red-500" data-id="element-3079" /> Afgekeurde
                Vragen
                {topBlockers.length > 0 && <span className="text-xs font-normal text-gray-500" data-id="element-3080">
                    (top {topBlockers.length})
                  </span>}
              </h3>
              {topBlockers.length > 0 && <button onClick={() => onNavigateToQuestion('blockers')} className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1" data-id="element-3081">
                  Bekijk alle {stats.fail} <ArrowRight className="w-3 h-3" data-id="element-3082" />
                </button>}
            </div>

            {topBlockers.length === 0 ? <div className="bg-gray-50 rounded-lg p-8 text-center border border-gray-100" data-id="element-3083">
                <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" data-id="element-3084" />
                <p className="text-sm font-medium text-gray-700" data-id="element-3085">
                  Geen afgekeurde vragen!
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-3086">
                  Alle vragen komen door de pipeline.
                </p>
              </div> : <div className="space-y-2" data-id="element-3087">
                {topBlockers.map(row => {
              const firstBlocker = row.pipelineIssues.find(i => i.kind === 'blocker');
              const modConfig = MODULE_CONFIG[row.moduleCode] || {
                bgClass: 'bg-gray-500'
              };
              return <div key={row.questionId} className="flex items-start gap-3 p-3 rounded-lg border border-red-100 bg-red-50/50 hover:bg-red-50 transition-colors cursor-pointer" onClick={() => onNavigateToQuestion(row.questionId)} data-id="element-3088">
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${modConfig.bgClass}`} data-id="element-3089" />
                      <div className="min-w-0 flex-1" data-id="element-3090">
                        <div className="flex items-center gap-2 mb-1" data-id="element-3091">
                          <span className="font-mono text-xs font-bold text-gray-900" data-id="element-3092">
                            {row.questionId}
                          </span>
                          <Badge variant="danger" size="sm" data-id="element-3093">
                            FAIL
                          </Badge>
                          <span className="text-[10px] text-gray-500" data-id="element-3094">
                            {row.blockerCount} blocker
                            {row.blockerCount !== 1 ? 's' : ''}
                            {row.warningCount > 0 && ` · ${row.warningCount} verbeterpunt${row.warningCount !== 1 ? 'en' : ''}`}
                          </span>
                        </div>
                        {firstBlocker && <p className="text-sm text-red-800 truncate" data-id="element-3095">
                            <span className="font-semibold text-xs uppercase mr-1" data-id="element-3096">
                              [{firstBlocker.domain}]
                            </span>
                            {firstBlocker.message}
                          </p>}
                      </div>
                    </div>;
            })}
              </div>}
          </Card>

          {/* Pipeline Stages — individual findings per stage */}
          <Card className="p-5" data-id="element-3097">
            <div className="mb-4" data-id="element-3098">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-3099">
                <Zap className="w-4 h-4 text-amber-500" data-id="element-3100" /> Pipeline Fasen
              </h3>
              <p className="text-[11px] text-gray-500 mt-1" data-id="element-3101">
                Aantal individuele bevindingen per controle-fase (één vraag kan
                meerdere bevindingen opleveren).
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-3102">
              {PIPELINE_STAGES.map(stage => {
              const count = pipelineIssueCounts?.[stage.domain] ?? 0;
              const status = !pipelineIssueCounts ? 'pending' : count === 0 ? 'ok' : count <= 5 ? 'warning' : 'error';
              const statusConfig = {
                ok: {
                  dot: 'bg-emerald-500',
                  bg: 'bg-emerald-50'
                },
                warning: {
                  dot: 'bg-amber-500',
                  bg: 'bg-amber-50'
                },
                error: {
                  dot: 'bg-red-500',
                  bg: 'bg-red-50'
                },
                pending: {
                  dot: 'bg-gray-300',
                  bg: 'bg-gray-50'
                }
              }[status];
              return <div key={stage.name} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 bg-gray-50/50" data-id="element-3103">
                    <div className="flex items-center gap-3" data-id="element-3104">
                      <div className={`w-2 h-2 rounded-full ${statusConfig.dot}`} data-id="element-3105" />
                      <span className="text-sm font-medium text-gray-700" data-id="element-3106">
                        {stage.label}
                      </span>
                    </div>
                    {status !== 'pending' && <Badge variant={status === 'ok' ? 'success' : status === 'warning' ? 'warning' : 'danger'} size="sm" data-id="element-3107">
                        {count === 0 ? '0 bevindingen' : `${count} bevindingen`}
                      </Badge>}
                  </div>;
            })}
            </div>
          </Card>
        </div>

        {/* Right Column: Module Health */}
        <div className="space-y-6" data-id="element-3108">
          <Card className="p-5" data-id="element-3109">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-3110">
              <FileText className="w-4 h-4 text-blue-500" data-id="element-3111" /> Module Gezondheid
            </h3>
            <div className="space-y-3" data-id="element-3112">
              {moduleHealth.map(mod => {
              const config = MODULE_CONFIG[mod.code];
              const bgClass = config?.bgClass ?? 'bg-gray-400';
              const isExpanded = expandedModules.has(mod.code);
              const families = familyHealthByModule[mod.code] || [];
              return <div key={mod.code} className="space-y-1.5 border border-gray-100 rounded-lg p-2 bg-white" data-id="element-3113">
                    <div className="flex items-center justify-between text-xs cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors" onClick={() => toggleModule(mod.code)} data-id="element-3114">
                      <div className="flex items-center gap-1.5 font-medium text-gray-700" data-id="element-3115">
                        {isExpanded ? <ChevronDown className="w-3 h-3 text-gray-400" data-id="element-3116" /> : <ChevronRight className="w-3 h-3 text-gray-400" data-id="element-3117" />}
                        <div className={`w-1.5 h-1.5 rounded-full ${bgClass}`} data-id="element-3118" />
                        {mod.code}
                      </div>
                      <div className="flex items-center gap-2" data-id="element-3119">
                        <span className="text-gray-500" data-id="element-3120">{mod.total} vr</span>
                        <span className={`font-bold ${mod.pct === 100 ? 'text-emerald-600' : mod.pct >= 80 ? 'text-amber-600' : 'text-red-600'}`} data-id="element-3121">
                          {mod.pct}%
                        </span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mx-1" data-id="element-3122">
                      <div className={`h-full rounded-full ${mod.pct === 100 ? 'bg-emerald-500' : mod.pct >= 80 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                    width: `${mod.pct}%`
                  }} data-id="element-3123" />
                    </div>

                    {isExpanded && families.length > 0 && <div className="mt-2 pt-2 border-t border-gray-100 space-y-2 pl-4 pr-1" data-id="element-3124">
                        {families.map(fam => <div key={fam.familyId} className="space-y-1" data-id="element-3125">
                            <div className="flex items-center justify-between text-[10px]" data-id="element-3126">
                              <span className="font-mono text-gray-600 truncate mr-2" title={fam.familyId} data-id="element-3127">
                                {fam.familyId}
                              </span>
                              <div className="flex items-center gap-2 flex-shrink-0" data-id="element-3128">
                                <span className="text-gray-400" data-id="element-3129">
                                  {fam.pass}/{fam.total}
                                </span>
                                <span className={`font-semibold ${fam.pct === 100 ? 'text-emerald-600' : fam.pct >= 80 ? 'text-amber-600' : 'text-red-600'}`} data-id="element-3130">
                                  {fam.pct}%
                                </span>
                              </div>
                            </div>
                            <div className="h-1 bg-gray-100 rounded-full overflow-hidden" data-id="element-3131">
                              <div className={`h-full rounded-full ${fam.pct === 100 ? 'bg-emerald-400' : fam.pct >= 80 ? 'bg-amber-400' : 'bg-red-400'}`} style={{
                        width: `${fam.pct}%`
                      }} data-id="element-3132" />
                            </div>
                          </div>)}
                      </div>}
                  </div>;
            })}
            </div>
          </Card>
        </div>
      </div>
    </div>;
}